// Extracted from dashboard-layout.tsx (gap-fill for Plan
// 2026-05-06-meta-oauth-server-side-state, Task 8 follow-up commit a3b2c863).
//
// `launchMetaSignup` can throw — most commonly when /meta/oauth/start returns
// non-2xx (e.g. 401 stale token, 5xx Meta config issue) or when something
// upstream of the popup open fails. The handler swallows + reports because:
//   - Sentry breadcrumb via captureException (so we see it in fatal triage).
//   - User-facing toast so they know the action didn't take effect — they'd
//     otherwise be staring at a button that did nothing.
//
// Same-window mobile redirect path also benefits: there's no popup-relay
// flow on mobile, so toast is the only feedback channel for early failure.
//
// Lives in its own module so the toast-on-failure assertion can run without
// rendering the full DashboardLayout tree. DashboardLayout is the sole
// consumer.
import { useCallback } from 'react';
import { toast } from 'sonner';
import { AppError } from '@hookmyapp/observability/errors';
import { emit } from '@/lib/analytics';
import type { WorkspacePublicId } from '@hookmyapp/shared';
import { launchMetaSignup } from '@/lib/meta-signup';
import { captureException } from '@/lib/sentry';

type FetchWithAuth = (path: string, options?: RequestInit) => Promise<Response>;

/**
 * Returns a stable click handler that calls `launchMetaSignup`. On failure,
 * captures the exception to Sentry and shows a `toast.error` so the user
 * gets feedback (especially on the same-window mobile redirect path where
 * popup-relay UX doesn't apply).
 *
 * Funnel events: `whatsapp_connect_clicked` (legacy) + `meta_oauth_started`
 * (Phase 113 funnel stage 6) fire on every click before `launchMetaSignup`
 * runs. Both events were originally in the (now-removed) ConnectWhatsAppButton
 * wrapper; moved here when the wrapper was deleted so the live click path
 * remains instrumented.
 */
export function useConnectMetaSignup(
  workspaceTarget:
    | WorkspacePublicId
    | (() => Promise<WorkspacePublicId>)
    | null
    | undefined,
  fetchWithAuth: FetchWithAuth,
): () => Promise<boolean> {
  return useCallback(async () => {
    if (!workspaceTarget) return false;
    emit('whatsapp_connect_clicked', {});
    emit('meta_oauth_started', {
      config_id: import.meta.env.VITE_META_CONFIG_ID,
    });
    try {
      await launchMetaSignup(workspaceTarget, fetchWithAuth);
      return true;
    } catch (e) {
      // Surface a Sentry breadcrumb + toast so the user gets feedback even on
      // the same-window redirect path (mobile) where popup-relay UX doesn't apply.
      captureException(e);
      toast.error(
        e instanceof AppError && e.userMessage
          ? e.userMessage
          : "We couldn't connect you to Meta right now. Please try again in a moment.",
      );
      return false;
    }
  }, [workspaceTarget, fetchWithAuth]);
}
